import { useCallback, useRef } from "react";
import { streamChat, type LlmMessage } from "../llm/client";
import { dispatchTool } from "../llm/tools";
import { useChatStore } from "../state/chat";
import { usePortfolioStore } from "../state/portfolio";
import type { ProposeInterventionInput, ShowOnPanelInput } from "../llm/toolSchemas";

interface InvokedTool {
  name: string;
  input: unknown;
}

function summarizeTools(
  tools: InvokedTool[],
  merchantNameById: Map<string, string>
): string {
  if (tools.length === 0) return "";
  const parts: string[] = [];
  for (const t of tools) {
    if (t.name === "show_on_panel") {
      const input = t.input as ShowOnPanelInput;
      const focus = input.focus;
      switch (focus.kind) {
        case "portfolio":
          parts.push("◈ opened the portfolio view on the right.");
          break;
        case "merchant": {
          const name = focus.merchantId
            ? merchantNameById.get(focus.merchantId) ?? focus.merchantId
            : "the merchant profile";
          parts.push(`◈ focused on ${name}.`);
          break;
        }
        case "ethoca":
          parts.push("◈ pulled the ethoca / cdrn queue.");
          break;
        case "tc40":
          parts.push("◈ opened the ce 3.0 match map.");
          break;
        case "cohort":
          parts.push(
            focus.filter
              ? `◈ filtered the cohort grid by ${focus.filter}.`
              : "◈ opened the full merchant cohort."
          );
          break;
        case "retention":
          parts.push("◈ opened the retention forecast.");
          break;
        case "intel-graph":
          parts.push("◈ opened the cross-merchant intel graph.");
          break;
      }
    } else if (t.name === "propose_intervention") {
      const input = t.input as ProposeInterventionInput;
      const name =
        merchantNameById.get(input.merchant_id) ?? input.merchant_id;
      parts.push(
        `⬡ proposing ${input.kind}${input.level_pct ? ` @ ${input.level_pct}%` : ""} on ${name}.`
      );
    }
  }
  return parts.join(" ");
}

export function useLLMStream() {
  const append = useChatStore((s) => s.append);
  const appendToken = useChatStore((s) => s.appendToken);
  const setEntryText = useChatStore((s) => s.setEntryText);
  const setStreaming = useChatStore((s) => s.setStreaming);
  const setMode = useChatStore((s) => s.setMode);
  const entries = useChatStore((s) => s.entries);
  const portfolio = usePortfolioStore((s) => s.portfolio);
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (userText: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const transcript: LlmMessage[] = [];
      for (const entry of entries) {
        if (entry.kind === "ripper" && entry.text) {
          transcript.push({ role: "assistant", content: entry.text });
        } else if (entry.kind === "user") {
          transcript.push({ role: "user", content: entry.text });
        } else if (entry.kind === "receipt") {
          transcript.push({ role: "user", content: `[receipt] ${entry.text}` });
        }
      }
      transcript.push({ role: "user", content: userText });

      const assistantId = `ripper-${Date.now()}`;
      append({ id: assistantId, kind: "ripper", text: "", streaming: true });
      setMode("thinking");

      const merchantNameById = new Map(
        portfolio.merchants.map((m) => [m.id, m.name])
      );
      const invokedTools: InvokedTool[] = [];
      let textReceived = false;

      try {
        await streamChat({
          messages: transcript,
          portfolio,
          signal: controller.signal,
          onEvent: (event) => {
            if (event.type === "text" && event.text) {
              textReceived = true;
              appendToken(assistantId, event.text);
            } else if (event.type === "tool_use" && event.toolName) {
              invokedTools.push({
                name: event.toolName,
                input: event.toolInput ?? {},
              });
              dispatchTool({
                id: event.toolId ?? `${assistantId}-tool`,
                name: event.toolName,
                input: event.toolInput ?? {},
              });
            } else if (event.type === "error") {
              textReceived = true;
              appendToken(
                assistantId,
                `\n\n[ripper] connection hiccup — ${event.message ?? "unknown"}. try again?`
              );
            }
          },
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          textReceived = true;
          appendToken(assistantId, `\n\n[ripper] chat error: ${(err as Error).message}`);
        }
      } finally {
        // If Claude acted without narrating, fill in a minimal acknowledgement
        // so the chat doesn't render an empty bubble.
        if (!textReceived) {
          const summary = summarizeTools(invokedTools, merchantNameById);
          setEntryText(
            assistantId,
            summary || "◈ updated the panel on the right."
          );
        }
        setStreaming(assistantId, false);
        setMode("listening");
      }
    },
    [append, appendToken, entries, portfolio, setEntryText, setMode, setStreaming]
  );

  return { send, cancel: () => abortRef.current?.abort() };
}
